import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Environment } from 'environments/environment.interface';

interface EnvironmentInfo {
  name: string;
  production: boolean;
  development: boolean;
  staging: boolean;
  projectId: string;
  features: {
    debugMode: boolean;
    mockData: boolean;
    testingTools: boolean;
    consoleLogging: boolean;
  };
}

// Cast environment once with the full Environment type that includes optional properties
const env = environment as Environment;

@Injectable({
  providedIn: 'root'
})
export class EnvironmentService {

  get isProduction(): boolean {
    return env.production === true;
  }

  get isDevelopment(): boolean {
    return env.development === true;
  }

  get isStaging(): boolean {
    return env.staging === true;
  }

  get environmentName(): string {
    return env.environmentName || 'UNKNOWN';
  }

  get showDebugInfo(): boolean {
    return env.ui?.showDebugInfo || false;
  }

  get enableMockData(): boolean {
    return env.ui?.enableMockData || false;
  }

  get enableTestingTools(): boolean {
    return env.ui?.enableTestingTools || false;
  }

  get enableConsoleLogging(): boolean {
    return env.features?.enableConsoleLogging || false;
  }

  get firebaseProjectId(): string {
    return env.firebase.projectId;
  }

  /**
   * Log a message only if console logging is enabled
   */
  log(message: unknown, ...args: unknown[]): void {
    if (this.enableConsoleLogging) {
      console.log(message, ...args);
    }
  }

  /**
   * Log a warning only if console logging is enabled
   */
  warn(message: unknown, ...args: unknown[]): void {
    if (this.enableConsoleLogging) {
      console.warn(`[${this.environmentName}]`, message, ...args);
    }
  }

  /**
   * Log an error (always shown regardless of environment)
   */
  error(message: unknown, ...args: unknown[]): void {
    console.error(`[${this.environmentName}]`, message, ...args);
  }

  /**
   * Show environment information for debugging
   */
  getEnvironmentInfo(): EnvironmentInfo {
    return {
      name: this.environmentName,
      production: this.isProduction,
      development: this.isDevelopment,
      staging: this.isStaging,
      projectId: this.firebaseProjectId,
      features: {
        debugMode: this.showDebugInfo,
        mockData: this.enableMockData,
        testingTools: this.enableTestingTools,
        consoleLogging: this.enableConsoleLogging
      }
    };
  }
  
  /**
   * Check if we're running in a safe environment for admin operations
   */
  isSafeForAdminOperations(): boolean {
    // Admin operations are safe in development and staging
    return this.isDevelopment || this.isStaging;
  }
  
  /**
   * Get environment-specific warning message for production operations
   */
  getProductionWarningMessage(): string | null {
    if (this.isProduction) {
      return '⚠️ You are operating in PRODUCTION environment. All changes will affect live users!';
    }
    return null;
  }
} 